<template>
	<div ref="graphRef" :style="graphStyle">
		<GraphlyD3 ref="graphly" :dark="false" :env-gravity="0" :draggableNodes="false" />
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, onMounted } from 'vue';
	import { type Link } from '@livereader/graphly-d3';
	import GraphlyD3 from '@livereader/graphly-d3/component/vue3';
	import { Event, ForceSimulation, renderTemplate, type Graph, type Node } from '@livereader/graphly-d3';
	import '@livereader/graphly-d3/style.css';

	const graphRef: Ref<HTMLElement | null> = ref(null);
	const graphHeight: Ref<number> = ref(0);
	const graphWidth: Ref<number> = ref(0);
	const linkColor = '#00ffea';

	type Orientation = 'Top' | 'Bottom' | 'Left' | 'Right';

	const props = defineProps({
		width: {
			type: [String, Number],
			default: '600px',
		},
		height: {
			type: [String, Number],
			default: '400px',
		},
		data: {
			type: [String],
			default: '',
		},
		graph: {
			type: Object as PropType<Graph>,
			default: () => ({
				nodes: [],
				links: [],
			}),
		},
		orientation: {
			type: String, // Using String as the prop type
			default: 'Top',
			validator: (value: string): value is Orientation => {
				return ['Top', 'Bottom', 'Left', 'Right'].includes(value);
			}
		},
	});

	const graphStyle = computed(() => ({
		width: props.width,
		height: props.height,
	}));

	const graphly = ref<any>(null);

	const simulation = computed<ForceSimulation>(() => graphly.value.simulation);
	const scale = 0.6;

	const updateGraphCoordinates = (graph: Graph, orientation: Orientation = 'Top') => {
		const widthOfGraphPx = graphWidth.value; // Width of the graph
		const heightSpacing = graphHeight.value / 3; // 3 levels based on the given graph

		// ... [rest of your code, no change here]
		// Construct tree
		const tree: { [key: string]: string[] } = {};
		graph.links.forEach((link) => {
			const sourceId = typeof link.source === 'string' ? link.source : (link.source as Node).id;
			const targetId = typeof link.target === 'string' ? link.target : (link.target as Node).id;

			if (!tree[sourceId]) tree[sourceId] = [];
			tree[sourceId].push(targetId);
		});
		// Calculate depth for each node
		const getDepth = (id: string, depth = 0): number => {
			if (!tree[id] || tree[id].length === 0) return depth;
			return Math.max(...tree[id].map((childId) => getDepth(childId, depth + 1)));
		};

		function assignNodePositions(nodeId: string, x: number, y: number, layerWidth: number): void {
			const node = graph.nodes.find((n) => n.id === nodeId);
			if (node) {
				node.x = x;
				node.y = y;

				if (tree[nodeId]) {
					const children = tree[nodeId];
					const childSpacing = layerWidth / (children.length + 1);

					children.forEach((childId, index) => {
						let newX = x;
						let newY = y;

						switch (orientation) {
							case 'Top':
								newX = x - layerWidth / 2 + (index + 1) * childSpacing;
								newY = y + heightSpacing;
								break;
							case 'Bottom':
								newX = x - layerWidth / 2 + (index + 1) * childSpacing;
								newY = y - heightSpacing;
								break;
							case 'Left':
								newY = y - layerWidth / 2 + (index + 1) * childSpacing;
								newX = x + heightSpacing;
								break;
							case 'Right':
								newY = y - layerWidth / 2 + (index + 1) * childSpacing;
								newX = x - heightSpacing;
								break;
						}

						assignNodePositions(childId, newX, newY, childSpacing);
					});
				}
			}
		}

		function propagateColor(nodeId: string, parentColor?: string): void {
			// Find the corresponding node in the graph's nodes array
			const node = graph.nodes.find((n) => n.id === nodeId);

			if (node) {
				// If the node doesn't have a color, use the parent's color
				if (!node.payload.color && parentColor) {
					node.payload.color = parentColor;
				}

				// Recursively propagate color to child nodes
				if (tree[nodeId]) {
					tree[nodeId].forEach((childId) => propagateColor(childId, node.payload.color));
				}
			}
		}
		if(graph.nodes.length > 0) {
			const rootNodeId = graph.nodes[0].id;
			assignNodePositions(rootNodeId, 0, graphHeight.value / -2 + 42, widthOfGraphPx);
			propagateColor(rootNodeId);
		}
	};

	const reactiveGraph = computed(() => {
		if (props.data.length > 0) {
			try {
			return JSON.parse(props.data);
			} catch (e) {
			console.error('Error parsing JSON:', e);
			return props.graph; // Fallback to default graph in case of error
			}
		} else {
			return props.graph;
		}
	});
	
	watch(reactiveGraph, () => {
		updateGraphCoordinates(reactiveGraph.value, props.orientation as Orientation);
		simulation.value.render(reactiveGraph.value);
	});

	onMounted(async () => {
		if (graphRef.value) {
			console.log('Height of the div:', graphRef.value.clientHeight, 'pixels');
			graphHeight.value = graphRef.value.clientHeight;
			graphWidth.value = graphRef.value.clientWidth;
		}
		simulation.value.templateStore.add('hexagon', useHexagon());
		simulation.value.templateStore.add('circle', useCircle());
		simulation.value.templateStore.add('icon', useIcon());
		updateGraphCoordinates(reactiveGraph.value, props.orientation as Orientation);
		simulation.value.render(reactiveGraph.value);
	});
</script>

<style lang="scss">
	svg .gly-node {
		fill: none !important;
		stroke-width: 8px;
		stroke: aqua;
	}
</style>
